import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { tenantApi } from '@/services/tenantService';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getTenantId, setTenantId, clearTenantContextStorage } from '@/services/tokenStorage';

interface Tenant {
  id?: string | number;
  name?: string;
  domain?: string;
  plan?: string;
  settings?: Record<string, any>;
  status?: boolean;
  trialEndsAt?: string;
  subscriptionEndsAt?: string;
  maxUsers?: number;
  storageQuota?: number;
  onboardingComplete?: boolean;
  onboardedAt?: string;
  [key: string]: any;
}

interface TenantUsage {
  usersCount: number;
  storageUsageMb: number;
  [key: string]: any;
}

interface TenantState {
  currentTenant: Tenant | null;
  tenantId: string | null;
  tenants: any[];
  tenantUsage: TenantUsage | null;
  loading: boolean;
  error: any | null;
  onboardingComplete: boolean;
  initialized: boolean;
}

const getInitialTenantId = () => {
  if (typeof window !== 'undefined') {
    return getTenantId();
  }
  return null;
};

const initialState: TenantState = {
  currentTenant: null,
  tenantId: getInitialTenantId(),
  tenants: [],
  tenantUsage: null,
  loading: false,
  error: null,
  onboardingComplete: false,
  initialized: false,
};

export const initializeTenantContext = createAsyncThunk(
  'tenant/initializeTenantContext',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { tenant: TenantState; auth?: { tenantId?: string | null } };
    // A fresh sign-in updates auth before this slice has a tenant. Prefer the
    // authenticated identity so initialization does not silently skip first login.
    const tenantId = state.auth?.tenantId ?? state.tenant.tenantId ?? getTenantId();
    if (!tenantId) {
      return null;
    }
    try {
      const response = await tenantApi.getCurrent();
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue({
        status: err?.response?.status,
        data: err?.response?.data || err.message,
      });
    }
  }
);

export const switchTenant = createAsyncThunk(
  'tenant/switchTenant',
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getById(id);
      return { id: id.toString(), tenant: response.data.data || response.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchCurrentTenant = createAsyncThunk(
  'tenant/fetchCurrentTenant',
  async (_, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getCurrent();
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTenantSettings = createAsyncThunk(
  'tenant/updateTenantSettings',
  async (data: Record<string, any>, { getState, rejectWithValue }) => {
    const state = getState() as { tenant: TenantState };
    try {
      const response = await tenantApi.updateSettings(state.tenant.tenantId!, data);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTenantUsage = createAsyncThunk(
  'tenant/fetchTenantUsage',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { tenant: TenantState };
    try {
      const response = await tenantApi.getUsage(state.tenant.tenantId!);
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTenants = createAsyncThunk(
  'tenant/fetchTenants',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getAll(params);
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTenantById = createAsyncThunk(
  'tenant/fetchTenantById',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await tenantApi.getById(id);
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const registerTenant = createAsyncThunk(
  'tenant/registerTenant',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await tenantApi.register(data);
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const checkTenantLimits = createAsyncThunk(
  'tenant/checkTenantLimits',
  async (_, { getState, dispatch }) => {
    const state = getState() as { tenant: TenantState };
    if (!state.tenant.tenantId) return;
    try {
      await dispatch(fetchTenantUsage()).unwrap();
    } catch (err) {
      // Non-blocking: usage stats are informational. Log in DEV only.
      if (import.meta.env.DEV) {
        console.error('[tenant] fetchTenantUsage failed during checkTenantLimits:', err);
      }
    }
  }
);

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<any>) {
      state.error = action.payload;
      state.loading = false;
    },
    clearError(state) {
      state.error = null;
    },
    setOnboardingComplete(state, action: PayloadAction<boolean>) {
      state.onboardingComplete = action.payload;
      if (state.currentTenant) {
        state.currentTenant.onboardingComplete = action.payload;
        if (action.payload) {
          state.currentTenant.onboardedAt = new Date().toISOString();
        }
      }
    },
    clearTenantContext(state) {
      state.currentTenant = null;
      state.tenantId = null;
      state.tenantUsage = null;
      state.tenants = [];
      state.error = null;
      state.onboardingComplete = false;
      state.initialized = false;
      clearTenantContextStorage();
    },
    reset(state) {
      state.currentTenant = null;
      state.tenantId = null;
      state.tenantUsage = null;
      state.tenants = [];
      state.error = null;
      state.onboardingComplete = false;
      state.initialized = false;
      clearTenantContextStorage();
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // initializeTenantContext
      .addCase(initializeTenantContext.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeTenantContext.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        if (action.payload) {
          state.currentTenant = action.payload;
          if (state.currentTenant?.id) {
            state.tenantId = state.currentTenant.id.toString();
            setTenantId(state.tenantId!);
          }
          state.onboardingComplete = state.currentTenant?.onboardingComplete === true;
        }
      })
      .addCase(initializeTenantContext.rejected, (state, action: any) => {
        if (action.payload?.status === 404 || action.payload?.status === 403) {
          state.currentTenant = null;
          state.tenantId = null;
          state.tenantUsage = null;
          state.tenants = [];
          state.error = null;
          state.onboardingComplete = false;
          state.initialized = false;
          clearTenantContextStorage();
          // Note: Redirection should be handled in components or middleware
        } else {
          state.error = action.payload?.data;
        }
        state.initialized = true;
        state.loading = false;
      })
      // switchTenant
      .addCase(switchTenant.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentTenant = null;
        state.tenantUsage = null;
        state.tenantId = action.meta.arg.toString();
        setTenantId(state.tenantId!);
      })
      .addCase(switchTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTenant = action.payload.tenant;
        state.onboardingComplete = state.currentTenant?.onboardingComplete === true;
        // NOTE: `window.location.reload()` used to run here. Navigation/side
        // effects do not belong in reducers; callers that need a full reload
        // after a tenant switch perform it themselves after the thunk resolves.
      })
      .addCase(switchTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchCurrentTenant
      .addCase(fetchCurrentTenant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTenant = action.payload;
        if (state.currentTenant?.id) {
          state.tenantId = state.currentTenant.id.toString();
          setTenantId(state.tenantId!);
        }
        state.onboardingComplete = state.currentTenant?.onboardingComplete === true;
      })
      .addCase(fetchCurrentTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // updateTenantSettings
      .addCase(updateTenantSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTenantSettings.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentTenant) {
          state.currentTenant = { ...state.currentTenant, ...action.payload };
        }
      })
      .addCase(updateTenantSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchTenantUsage
      .addCase(fetchTenantUsage.fulfilled, (state, action) => {
        state.tenantUsage = action.payload;
      })
      .addCase(fetchTenantUsage.rejected, (state, action) => {
        state.error = action.payload;
      })
      // fetchTenants
      .addCase(fetchTenants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.loading = false;
        state.tenants = action.payload;
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchTenantById
      .addCase(fetchTenantById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenantById.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchTenantById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // registerTenant
      .addCase(registerTenant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerTenant.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTenant = action.payload;
        if (state.currentTenant?.id) {
          state.tenantId = state.currentTenant.id.toString();
          setTenantId(state.tenantId!);
        }
      })
      .addCase(registerTenant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setLoading, setError, clearError, setOnboardingComplete, clearTenantContext, reset } = tenantSlice.actions;

export default tenantSlice.reducer;

export const selectTenantId = (state: any) => state?.tenant?.tenantId;

export const selectCurrentTenant = (state: any) => state?.tenant?.currentTenant;

/**
 * Pinia-compat bridge hook for migrated Vue components that expect a
 * `useTenantStore()` style API. Returns synchronous state getters plus
 * bound action dispatchers. Reads from the Redux `tenant` slice.
 */
export const useTenantStore = () => {
  const dispatch = useAppDispatch();
  const currentTenant = useAppSelector((state: any) => state.tenant.currentTenant);
  const tenantId = useAppSelector((state: any) => state.tenant.tenantId);
  const tenantUsage = useAppSelector((state: any) => state.tenant.tenantUsage);

  const daysUntilExpiry = useMemo<number | null>(() => {
    const t = currentTenant as Tenant | null;
    if (!t) return null;
    const expiryStr = t.subscriptionEndsAt || t.trialEndsAt;
    if (!expiryStr) return null;
    const ms = new Date(expiryStr).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [currentTenant]);

  return {
    getCurrentTenant: currentTenant,
    getTenantId: tenantId,
    tenantUsage,
    daysUntilExpiry,
    fetchCurrentTenant: () => dispatch(fetchCurrentTenant()),
    fetchTenantUsage: () => dispatch(fetchTenantUsage()),
    updateTenantSettings: (data: Record<string, any>) => dispatch(updateTenantSettings(data)),
    clearTenantContext: () => dispatch(clearTenantContext()),
  };
};
